import React, { useEffect, useState } from "react";
import "./ScanResultDetails.css";
import { SELECTION_STYLE, collect, num, overflowNotes, sagKey, sagOf, withDiameter } from "./scanResult";

export { collect };

/**
 * Renders a full scan result.
 *
 * Fields are rendered from an explicit allowlist rather than by iterating the
 * stored object. Results written before de-identification still carry a "file"
 * key holding the original Pentacam export name, which is
 * LASTNAME_FIRSTNAME_EYE_DATE -- iterating would put that on screen. Anything
 * not named here is not displayed.
 */

const Field = ({ label, value, unit, hint }) => (
  <div className="srd-field">
    <span className="srd-field-label">{label}</span>
    <span className="srd-field-value">
      {value == null ? <em className="srd-missing">not reported</em> : value}
      {value != null && unit ? <span className="srd-unit">{unit}</span> : null}
    </span>
    {hint ? <span className="srd-field-hint">{hint}</span> : null}
  </div>
);

export default function ScanResultDetails({ parameters, createdAt, code }) {
  const base = collect(parameters);
  const [chosen, setChosen] = useState(null);

  // a different result means a different set of diameters
  useEffect(() => setChosen(null), [parameters]);

  const [copied, setCopied] = useState(false);
  const options = base.options;
  const active =
    options && (options.find((o) => o["Lens Diameter"] === chosen) ||
                options.find((o) => o["Recommended"]) || options[0]);
  const r = withDiameter(base, active);
  const thin = typeof r.coverage === "number" && r.coverage < 0.995;

  // The quadrant-specific order's sag is the option's own; the toric order
  // carries its own inside "Lens Toricity". They differ when one quadrant is
  // past a limit that its meridian's average is not.
  const quadrantOrder = r.biElevation ? { "Bi-Elevation": r.biElevation } : { "Lens Sag": r.sag };
  const toricOrder = r.toricity && (r.toricity["Bi-Elevation"] || r.toricity["Lens Sag"] != null)
    ? r.toricity : null;
  const ordersDiffer = toricOrder != null && sagKey(toricOrder) !== sagKey(quadrantOrder);
  const toricAsked = r.toricity && r.toricity.requested;

  return (
    <div className="srd">
      <div className="srd-header">
        <div className="srd-header-main">
          {r.eye ? <span className="srd-eye">{r.eye}</span> : null}
          {code && (
            <button
              type="button"
              className="srd-code"
              title="Copy this reference"
              onClick={() => {
                if (navigator.clipboard?.writeText) {
                  navigator.clipboard.writeText(code).then(
                    () => { setCopied(true); setTimeout(() => setCopied(false), 1600); },
                    () => {}
                  );
                }
              }}
            >
              {copied ? "copied" : code}
            </button>
          )}
          <span className="srd-date">
            {createdAt ? new Date(createdAt).toLocaleString() : ""}
          </span>
        </div>
        {(r.scanTypes.length > 0 || r.device) && (
          <div className="srd-sources">
            {r.device && <span className="srd-source srd-device">{r.device}</span>}
            {r.scanTypes.map((t) => (
              <span key={t} className="srd-source">{t}</span>
            ))}
          </div>
        )}
      </div>

      {options && options.length > 1 && (
        <section className="srd-section">
          <h4 className="srd-section-title">Diameter</h4>
          <div className="srd-dia">
            {options.map((o) => {
              const on = o["Lens Diameter"] === r.diameter;
              const kind = SELECTION_STYLE[o["Selection"]] || SELECTION_STYLE.unknown;
              return (
                <button
                  key={o["Lens Diameter"]}
                  type="button"
                  className={`srd-dia-opt srd-dia-${kind.slug}${on ? " srd-dia-on" : ""}`}
                  onClick={() => setChosen(o["Lens Diameter"])}
                  aria-pressed={on}
                  title={o["Selection Basis"] || ""}
                >
                  <span className="srd-dia-mm">{o["Lens Diameter"].toFixed(1)}</span>
                  <span className="srd-dia-tag">{kind.label}</span>
                  {typeof o["Dot Clearance"] === "number" && (
                    <span className="srd-dia-dots">
                      dots {o["Dot Clearance"] > 0 ? "+" : ""}
                      {o["Dot Clearance"].toFixed(2)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {active && active["Selection Basis"] && (
            <p className={`srd-note srd-basis srd-basis-${(SELECTION_STYLE[active["Selection"]] || SELECTION_STYLE.unknown).slug}`}>
              <strong>{(SELECTION_STYLE[active["Selection"]] || SELECTION_STYLE.unknown).label}</strong>
              {" — "}{active["Selection Basis"]}.
            </p>
          )}

          {r.diameterSelection && r.diameterSelection.disclaimer && (
            <p className="srd-note srd-note-warn">{r.diameterSelection.disclaimer}</p>
          )}

          {r.diameterSelection &&
            r.diameterSelection.agreed &&
            r.diameterSelection.agreed.length === 0 &&
            r.diameterSelection.by_drill_dots &&
            r.diameterSelection.by_drill_dots.length === 0 &&
            r.diameterSelection.HVID != null && (
              <p className="srd-note srd-note-warn">
                No diameter places the limbal drill dots within the guide's target for an
                HVID of {r.diameterSelection.HVID.toFixed(1)} mm. Every option either sits
                inside the limbus or more than 0.5 mm outside it.
              </p>
            )}
          {r.recommended === false && (
            <p className="srd-note srd-note-warn">
              Showing an alternative to the {options.find((o) => o["Recommended"])?.["Lens Diameter"].toFixed(1)} mm
              the software selected for this eye.
            </p>
          )}
          {thin && (
            <p className="srd-note srd-note-warn">
              Only {Math.round(r.coverage * 100)}% of the sampling ring carried data at this
              diameter, so these figures rest on a thinner sample.
            </p>
          )}
        </section>
      )}

      <section className="srd-section">
        <h4 className="srd-section-title">Lens Specification</h4>
        <div className="srd-grid">
          <Field label="Lens Diameter" value={num(r.diameter, 1)} unit="mm" />
          <Field
            label={`${r.biElevation ? "Lens Sag (bi-elevation)" : "Lens Sag"}${ordersDiffer ? " · quadrant-specific" : ""}`}
            value={sagOf(quadrantOrder)}
            unit="µm"
            hint={[
              typeof r.clearance === "number"
                ? `includes ${r.clearance} µm central clearance`
                : null,
              r.biElevationReason ? `bi-elevation: ${r.biElevationReason}` : null,
            ].filter(Boolean).join(" · ") || null}
          />
          {ordersDiffer && (
            <Field
              label={`${toricOrder["Bi-Elevation"] ? "Lens Sag (bi-elevation)" : "Lens Sag"} · toric`}
              value={sagOf(toricOrder)}
              unit="µm"
              hint={toricOrder["Bi-Elevation"] ? `bi-elevation: ${toricOrder["Bi-Elevation"].reason}` : null}
            />
          )}
          <Field
            label="Base Curve"
            value={num(r.baseCurve, 2)}
            unit="mm"
            hint={r.midClearance != null ? `leaves ${r.midClearance} µm mid-peripheral clearance` : null}
          />
          {r.opticZone != null && (
            <Field label="Optic Zone Diameter" value={num(r.opticZone, 2)} unit="mm" />
          )}
        </div>
        {ordersDiffer && (
          <p className="srd-note">
            The toric and quadrant-specific orders need different sags: each carries
            its own steps past the manufacturer's limits into the bi-elevation. Order
            the sag that goes with the steps you order.
          </p>
        )}
      </section>

      {(r.bfs != null || r.hwtw != null) && (
        <section className="srd-section">
          <h4 className="srd-section-title">Cornea</h4>
          <div className="srd-grid">
            {r.bfs != null && (
              <Field label="Best-Fit Sphere" value={num(r.bfs, 2)} unit="mm" />
            )}
            {r.hwtw != null && (
              <Field label="HWTW" value={num(r.hwtw, 2)} unit="mm" />
            )}
          </div>
        </section>
      )}

      <section className="srd-section">
        <h4 className="srd-section-title">Meridians</h4>
        <div className="srd-grid">
          <Field
            label="Steep Axis"
            value={r.rotation && r.rotation.steep != null ? `${num(r.rotation.steep, 0)}°` : null}
          />
          <Field
            label="Flat Axis"
            value={r.rotation && r.rotation.flat != null ? `${num(r.rotation.flat, 0)}°` : null}
          />
          <Field
            label="Toricity"
            value={
              r.toricity
                ? `F ${r.toricity["F steps"]}  /  S ${r.toricity["S steps"]}`
                : null
            }
            hint={toricAsked
              ? `steps of 30 µm · F ${toricAsked["F steps"]} / S ${toricAsked["S steps"]} asked for`
              : "steps of 30 µm"}
          />
        </div>
        {overflowNotes(r.toricity && r.toricity["Step Limits"], "Toric order").map((text) => (
          <p key={text} className="srd-note srd-note-warn">{text}</p>
        ))}
      </section>

      {r.quadrants && r.quadrants.length > 0 && (
        <section className="srd-section">
          <h4 className="srd-section-title">Quadrant Steps</h4>
          <div className="srd-quadrants">
            {r.quadrants.map((q, i) => {
              const std = q.steps === "STD";
              const steep = !std && String(q.steps).endsWith("S");
              return (
                <div
                  key={`${q.axis}-${i}`}
                  className={`srd-quad${std ? " srd-quad-std" : ""}${q.limited ? " srd-quad-capped" : ""}`}
                >
                  <span className="srd-quad-step">{q.steps}</span>
                  <span className="srd-quad-axis">{q.axis}</span>
                  <span className="srd-quad-kind">
                    {q.limited
                      ? `at limit · ${q.requested} requested`
                      : q.requested
                        ? `moved with the bi-elevation · ${q.requested} requested`
                        : std ? "standard" : steep ? "steeper" : "flatter"}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="srd-note">
            Steps of 30 µm. STD means no step on that quadrant.
            {r.limits &&
              ` Maximum ${r.limits["max steep"]} steep, ${r.limits["max flat"]} flat.`}
          </p>
          {overflowNotes(r.limits, ordersDiffer ? "Quadrant-specific order" : null).map((text) => (
            <p key={text} className="srd-note srd-note-warn">{text}</p>
          ))}
        </section>
      )}

    </div>
  );
}
